import {
	Autocomplete,
	CircularProgress,
	FormControl,
	Stack,
	TextField,
	Typography,
} from '@mui/material';
import React, {useEffect, useState} from 'react';
import {Controller, useFormContext} from 'react-hook-form';

import {isValid as isValidDate} from 'date-fns';
import {AlertInfo, MaskedInput} from 'presentation/components';
import {FeaturesEnum} from 'presentation/constants/featuresFlagEnum';
import {useFeatureFlag} from 'presentation/hooks/useFeatureFlag';
import ModalDatePicker from 'presentation/components/ModalDatePicker';
import {useAccountInformation, useMediaQueries} from 'presentation/pages/private/hooks';
import {RequestErrorModal} from 'presentation/pages/private/refund/components';
import {AccordionWithStatus} from 'presentation/pages/private/refund/components/AccordionWithStatus';
import {StatusEnum} from 'presentation/pages/private/refund/constants/accordionByStatus';
import {translations} from 'presentation/translations-new';
import {formatText, masks, parseDate, stringfyDate} from 'presentation/utils';
import {useGetBeneficiaries} from '../../../../hooks/useGetBeneficiaries';
import {useGetSpecialties} from '../../../../hooks/useGetSpecialties';
import {useGetRefundPlansQuery} from '../../../../queries/useGetRefundPlansQuery';
import {useAccordionExpandedStore} from '../../../../store/useAccordionExpandedStore';
import {AccordionExpandedEnum} from '../../../../types';
import {PatientDataSchema} from '../../validations';
import {PlanSelect} from './components/PlanSelect';

const {
	title,
	patient,
	patientPlaceholder,
	serviceDate,
	specialty,
	specialtyPlaceholder,
	chooseServiceDate,
} = translations['pt-br'].privateFlow.refund.receiptRegistrationPage.patientData;

const {helperText: planHelperText} =
	translations['pt-br'].privateFlow.refund.receiptRegistrationPage.planData;

const {responsibleWithotuBenefitsAlert} = translations['pt-br'].privateFlow.refund;

const {loading: loadingText, notFound: notFoundText} = translations['pt-br'].privateFlow.refund;

const {cancelIn, refundCanceledContract, requiredField} = translations['pt-br'].commons;

export const PatientData = () => {
	const [isServiceDateModalOpen, setIsServiceDateModalOpen] = useState(false);
	const [plansFetchErrorModal, setPlansFetchErrorModal] = useState(false);
	const {isTablet} = useMediaQueries();
	const accordionExpanded = useAccordionExpandedStore(state => state.accordion);
	const setAccordionExpanded = useAccordionExpandedStore(state => state.setAccordion);
	const {isResponsibleWithoutBenefits, planBeneficiaries} = useAccountInformation();
	const {isEnabled: isPreviousPlansEnabled} = useFeatureFlag(
		FeaturesEnum.PORTAL_FEATURE_REFUND_PREVIOUS_PLANS,
	);

	const {
		control,
		watch,
		setValue,
		trigger,
		register,
		clearErrors,
		formState: {errors},
	} = useFormContext<PatientDataSchema>();

	const serviceDateWatched = watch('serviceDate');
	const patientWatched = watch('patient');
	const planWatched = watch('plan');
	const serviceDateFormat = parseDate(serviceDateWatched);

	const {
		data: plansData,
		isFetching: isPlansFetching,
		isError: plansFetchError,
	} = useGetRefundPlansQuery({
		userCode: patientWatched?.userCode,
		enabled: isPreviousPlansEnabled && !!patientWatched?.userCode,
	});

	const planOptions = plansData?.content.history ?? [];
	const isPlanDisabled = isPreviousPlansEnabled && (!patientWatched?.userCode || isPlansFetching);
	const isServiceDateDisabled = isPreviousPlansEnabled && !planWatched?.planCode;

	const patientCancelled = planBeneficiaries?.find(
		beneficiary => beneficiary?.cdUsuarioC === patientWatched?.userCode,
	);

	const {
		loadingList: loadingListSpecialties,
		options: optionsSpecialties,
		open: openSpecialties,
		handleClose: handleCloseSpecialties,
		handleOpen: handleOpenSpecialties,
		fetchError: specialtiesFetchError,
		setFetchError: specialtiesSetFetchError,
		loadMore: specialtiesLoadMore,
		handleSearchChange: specialtiesHandleSearchChange,
	} = useGetSpecialties();

	const {
		loadingList,
		options,
		open,
		handleClose,
		handleOpen,
		fetchError: beneficiariesFetchError,
		setFetchError: beneficiariesSetFetchError,
	} = useGetBeneficiaries();

	const handleChangeServiceDate = (value: Date | null) => {
		const selectedDate = value === null ? ' ' : stringfyDate(value);
		setValue('serviceDate', selectedDate);
		trigger('serviceDate');
	};

	const handleOpenServiceDateModal = () => {
		if (!isServiceDateDisabled) {
			setIsServiceDateModalOpen(true);
		}
	};

	const formatPlanDate = (date: string) => {
		const [year, month, day] = date.split('-');
		return `${day}/${month}/${year}`;
	};

	const getInitialServiceCalendarDate = () => {
		if (isValidDate(serviceDateFormat)) {
			return undefined;
		}

		if (planWatched?.finalEffectiveDate) {
			return parseDate(planWatched.finalEffectiveDate, 'yyyy-MM-dd') as Date;
		}

		return undefined;
	};

	const getMaxServiceDate = () => {
		if (planWatched?.detail === 'ANTERIOR' && planWatched.finalEffectiveDate) {
			return parseDate(formatPlanDate(planWatched.finalEffectiveDate)) as Date;
		}

		if (patientCancelled?.dtCancelamentoC) {
			return parseDate(patientCancelled.dtCancelamentoC) as Date;
		}

		return new Date();
	};

	useEffect(() => {
		if (patientWatched?.userCode && serviceDateWatched) {
			trigger('serviceDate');
		}
		if (!patientWatched?.userCode) {
			clearErrors(['plan', 'serviceDate']);
		}
	}, [serviceDateWatched, patientWatched]);

	useEffect(() => {
		setValue('planDisabled', isPlanDisabled);
		if (isPlanDisabled) {
			clearErrors('plan');
		}
	}, [isPlanDisabled]);

	useEffect(() => {
		if (plansFetchError) {
			setPlansFetchErrorModal(true);
		}
	}, [plansFetchError]);

	useEffect(() => {
		if (planOptions.length !== 1 || !isPreviousPlansEnabled) {
			return;
		}

		const [singlePlan] = planOptions;
		if (singlePlan.planCode !== planWatched?.planCode) {
			setValue('plan', singlePlan);
			clearErrors('plan');
		}
	}, [planOptions, isPreviousPlansEnabled, planWatched]);

	const patientRequiredField = errors.patient ? requiredField : null;
	const hasPlanError = !isPlanDisabled && !!errors.plan;
	const planRequiredField = !isPlanDisabled && errors.plan ? requiredField : planHelperText;
	const serviceDateRequiredField = errors.serviceDate?.ref?.value === '' ? requiredField : null;
	const specialtyRequiredField = errors.specialty ? requiredField : null;

	const isAccordionExpanded =
		accordionExpanded > AccordionExpandedEnum.PATIENT_DATA
			? StatusEnum.FINISHED
			: StatusEnum.PENDING;

	const isFinished = isAccordionExpanded === StatusEnum.FINISHED;

	const isExpanded = accordionExpanded === AccordionExpandedEnum.PATIENT_DATA;

	const isAccordionCloseAndFinished = !isExpanded && isFinished;

	const handleEdit = () => {
		if (isAccordionCloseAndFinished) {
			setAccordionExpanded(AccordionExpandedEnum.PATIENT_DATA);
		}
	};

	const handleChangePatient = (
		selectedOptions: PatientDataSchema['patient'] | null,
		onChange: (selectedOptions: PatientDataSchema['patient'] | null) => void,
	) => {
		onChange(selectedOptions);
		clearErrors();

		if (isPreviousPlansEnabled) {
			setValue('plan', undefined);
			setValue('serviceDate', '');
		}
	};

	return (
		<AccordionWithStatus
			expanded={isExpanded}
			onClick={handleEdit}
			title={title}
			removeIcon
			cursor={isAccordionCloseAndFinished ? 'pointer' : 'default'}
			status={isAccordionExpanded}>
			<Stack gap={2} flex={1}>
				<Stack direction={'column'} spacing={2}>
					<Stack gap={1} flex={1}>
						<Typography color="primary" fontWeight={600}>
							{patient}
						</Typography>

						{isResponsibleWithoutBenefits ? (
							<AlertInfo.Root severity="info">
								<AlertInfo.Description>
									{responsibleWithotuBenefitsAlert}
								</AlertInfo.Description>
							</AlertInfo.Root>
						) : null}

						<Controller
							control={control}
							name="patient"
							render={({field: {value, onChange}}) => (
								<Autocomplete
									open={open}
									onOpen={handleOpen}
									onClose={handleClose}
									onChange={(_event, selectedOptions) => {
										handleChangePatient(selectedOptions, onChange);
									}}
									value={value?.userCode ? value : null}
									isOptionEqualToValue={(option, valueOption) =>
										option.userCode === valueOption.userCode
									}
									getOptionLabel={option =>
										option
											? formatText(option.fullSocialName || option.fullName)
											: ''
									}
									options={options ?? []}
									loading={loadingList}
									loadingText={loadingText}
									noOptionsText={notFoundText}
									renderInput={params => (
										<TextField
											{...params}
											placeholder={patientPlaceholder}
											label={patientPlaceholder}
											error={!!errors.patient}
											helperText={patientRequiredField}
											slotProps={{
												input: {
													...params.InputProps,
													endAdornment: (
														<React.Fragment>
															{loadingList ? (
																<CircularProgress
																	color="inherit"
																	size={20}
																/>
															) : null}
															{params.InputProps.endAdornment}
														</React.Fragment>
													),
												},
											}}
										/>
									)}
								/>
							)}
						/>
					</Stack>
					{isPreviousPlansEnabled ? (
						<PlanSelect
							planOptions={planOptions}
							isPlanDisabled={isPlanDisabled}
							isPlansFetching={isPlansFetching}
							hasPlanError={hasPlanError}
							helperText={planRequiredField}
						/>
					) : null}
					<Stack gap={1} flex={1}>
						<Typography color="primary" fontWeight={600}>
							{serviceDate}
						</Typography>
						{patientCancelled?.dtCancelamentoC ? (
							<AlertInfo.Root severity="warning" sx={{mb: 1}}>
								<AlertInfo.Description>
									<Typography
										fontSize={'0.875rem'}
										component={'span'}
										fontWeight={700}>
										{`${cancelIn}${patientCancelled.dtCancelamentoC}`}
									</Typography>
									<Typography component={'p'} fontSize={'inherit'}>
										{refundCanceledContract}
									</Typography>
								</AlertInfo.Description>
							</AlertInfo.Root>
						) : null}
						<MaskedInput
							{...register('serviceDate')}
							id="serviceDate"
							inputMode="text"
							error={!!errors?.serviceDate?.message}
							helperText={errors?.serviceDate?.message || serviceDateRequiredField}
							disabled={isServiceDateDisabled}
							fullWidth
							autoComplete="current-serviceDate"
							label={chooseServiceDate}
							placeholder={'DD/MM/AAAA'}
							$fieldName="serviceDate"
							maskOptions={masks.birthday}
							endAdornmentVariant="button"
							endAdornmentIcon="calendar-focused-filled"
							handleAction={handleOpenServiceDateModal}
						slotProps={{
							inputLabel: {
								shrink: true,
							},
						}}
						/>
					</Stack>
				</Stack>
				<Stack direction={!isTablet ? 'row' : 'column'} spacing={2}>
					<Stack gap={1} flex={1}>
						<Typography color="primary" fontWeight={600}>
							{specialty}
						</Typography>
						<FormControl>
							<Controller
								control={control}
								name="specialty"
								render={({field: {value, onChange}}) => (
									<Autocomplete
										open={openSpecialties}
										onOpen={handleOpenSpecialties}
										onClose={handleCloseSpecialties}
										slotProps={{
											listbox: {
												onScroll: event => {
													const listboxNode = event.currentTarget;
													const bottom =
														listboxNode.scrollTop +
															listboxNode.clientHeight ===
														listboxNode.scrollHeight;
													if (bottom) {
														specialtiesLoadMore();
													}
												},
											},
										}}
										onChange={(_event, selectedOptions) => {
											onChange(selectedOptions);
											clearErrors();
										}}
										onInputChange={(_event, newInputValue, reason) => {
											if (reason === 'input') {
												specialtiesHandleSearchChange(newInputValue);
											}
										}}
										value={value?.code ? value : null}
										isOptionEqualToValue={(option, valueOption) =>
											option.code === valueOption.code
										}
										getOptionLabel={option =>
											option ? formatText(option.description) : ''
										}
										filterOptions={(specialties, {inputValue}) => {
											const inputValueTrimmed = inputValue
												.trim()
												.toLowerCase();
											return specialties.filter(option =>
												option.description
													.toLowerCase()
													.includes(inputValueTrimmed),
											);
										}}
										getOptionKey={option => option.description}
										options={optionsSpecialties}
										loading={loadingListSpecialties}
										loadingText={loadingText}
										noOptionsText={notFoundText}
										renderInput={params => (
											<TextField
												{...params}
												placeholder={specialtyPlaceholder}
												label={specialtyPlaceholder}
												error={!!errors.specialty}
												helperText={specialtyRequiredField}
												slotProps={{
													input: {
														...params.InputProps,
														endAdornment: (
															<React.Fragment>
																{loadingListSpecialties ? (
																	<CircularProgress
																		color="inherit"
																		size={20}
																	/>
																) : null}
																{params.InputProps.endAdornment}
															</React.Fragment>
														),
													},
												}}
											/>
										)}
									/>
								)}
							/>
						</FormControl>
					</Stack>
				</Stack>
			</Stack>

			<ModalDatePicker
				externalOpen={isServiceDateModalOpen}
				onAbandonModal={() => setIsServiceDateModalOpen(false)}
				selectFirstDate={handleChangeServiceDate}
				currentFirstDate={isValidDate(serviceDateFormat) ? serviceDateFormat : undefined}
				initialCalendarDate={getInitialServiceCalendarDate()}
				includeDateIntervals={{
					start: new Date(2000, 0, 1),
					end: getMaxServiceDate(),
				}}
				showClearButton
				hideOpenButton
				shouldHaveDateIntervals
			/>

			<RequestErrorModal open={specialtiesFetchError} setOpen={specialtiesSetFetchError} />

			<RequestErrorModal open={plansFetchErrorModal} setOpen={setPlansFetchErrorModal} />

			<RequestErrorModal
				open={beneficiariesFetchError}
				setOpen={beneficiariesSetFetchError}
			/>
		</AccordionWithStatus>
	);
};
